
// src/pages/shipment/ShippingManagement.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import '../../utils/patchResizeObserver';
import ShipmentList from "./ShipmentList";
import CreateShippingTabs from "./CreateShippingTabs";
import { useDispatch } from "react-redux";
import { setLoadTable } from "../../redux/action/skuTable.action";
import { toast } from "../../components/AppToast";
import { fetchShipment, getShipmentById } from "../../services/shipment/shipment"; // <-- your API

/* ---------- normalizeShipmentApi and helpers (unchanged logic) ---------- */
function normalizeShipmentApi(record = {}) {
    console.log("normalizeShipmentApi input record:", record);
    if (!record || typeof record !== "object") return { initialValues: {}, tableData: {} };

    const hdr = record.shipmentDetails ?? record.header ?? record.soDetails ?? {};
    const src = record.sourceDetail ?? record.source ?? record.from ?? {};
    const dest = record.destination ?? record.to ?? {};
    const billTo = record.billTo ?? record.bill_to ?? {};
    const invoice = record.invoice ?? hdr.invoice ?? {};
    const udf = record.udf ?? hdr.udf ?? {};
    const loadObj = record.load ?? record.loadDates ?? hdr.load ?? {};

    const carrRaw =
        record.carrier ??
        record.carrierDetail ??
        record.carrier_info ??
        (record.loading && (record.loading.carrier ?? record.loading)) ??
        (record.misc && record.misc.carrier) ??
        undefined;

    const carr = (() => {
        if (!carrRaw) return {};
        if (typeof carrRaw === "string") return { carrierName: carrRaw };
        if (typeof carrRaw === "object") {
            return {
                ...(carrRaw || {}),
                carrierName: carrRaw.carrierName ?? carrRaw.name ?? carrRaw.carrier_name ?? carrRaw.carrier ?? carrRaw.carrierName,
                addressLine1: carrRaw.addressLine1 ?? carrRaw.address_line1 ?? carrRaw.addr1 ?? carrRaw.address1,
            };
        }
        return {};
    })();

    const ensureDateIso = (v) => {
        if (v === null || v === undefined || v === "") return null;
        if (typeof v === "string") return v;
        if (typeof v === "number") return new Date(v).toISOString();
        if (v instanceof Date) return v.toISOString();
        if (typeof v === "object" && typeof v.toISOString === "function") return v.toISOString();
        return String(v);
    };

    const toNum = (v) => {
        if (v === null || v === undefined || v === "") return 0;
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const helper_getLineDeliveryState = (line = {}) => {
        try {
            const expected = toNum(line.orderedQty ?? line.expectedQty ?? line.quantity ?? line.qty);
            const delivered = toNum(
                line.receivedQty ?? line.deliveredQty ?? line.delivered ?? line.confirmedQty ?? line.receivedQuantity
            );
            const dispatched = toNum(line.dispatchedQty ?? line.dispatchQty ?? line.dispatched ?? line.shippedQty);
            const returned = toNum(line.returnedQty ?? line.returned ?? 0);
            const cancelled = toNum(line.cancelledQty ?? line.cancelled ?? 0);

            const explicit = (line.lineStatus ?? line.status ?? "").toString().toLowerCase();
            if (explicit) {
                if (explicit.includes("deliv") || explicit.includes("received") || explicit.includes("closed")) return "delivered";
                if (explicit.includes("partial")) return "partial";
                if (explicit.includes("dispatch") || explicit.includes("ship") || explicit.includes("inprogress")) return "in_progress";
                if (explicit.includes("cancel")) return "not_delivered";
            }

            if (expected > 0 && delivered >= Math.max(0, expected - returned) && cancelled === 0) return "delivered";
            if (delivered > 0 && delivered < expected) return "partial";
            if (dispatched > 0) return "in_progress";

            const podPresent = Boolean(line.podNumber || line.grNumber || line.podUpload || line.podFiles || line.deliveredAt || line.receivedDate);
            if (podPresent) return "delivered";

            return "not_delivered";
        } catch (e) {
            return "not_delivered";
        }
    };

    const helper_computeShipmentStatusFromLines = (lines = [], loadExists = false) => {
        try {
            const states = (lines || []).map(helper_getLineDeliveryState);
            const total = states.length;
            const deliveredCount = states.filter(s => s === "delivered").length;
            const partialCount = states.filter(s => s === "partial").length;
            const inProgCount = states.filter(s => s === "in_progress").length;

            if (total === 0) return "New";
            if (deliveredCount === total) return "Delivered";
            if (deliveredCount > 0 && deliveredCount < total) return "Partially Delivered";
            if (partialCount > 0) return "Partially Delivered";
            if (inProgCount > 0) return "In Progress";
            if (loadExists) return "In Transit";
            return "New";
        } catch (err) {
            return "New";
        }
    };

    const initialValues = {
        createUsing: hdr.createUsing ?? record.createUsing ?? null,
        shipmentNumber:
            hdr.shipmentNumber ??
            record.shipmentNumber ??
            (Array.isArray(record.shipmentHistory) && record.shipmentHistory[0]?.shipmentNumber) ??
            record.soNumber ??
            record.shipmentNo ??
            record._id ??
            null,
        externalShipmentNumber: hdr.externalShipmentNumber ?? hdr.externalId ?? record.externalShipmentNumber ?? record.externalId ?? null,
        loadId: hdr.loadId ?? record.loadId ?? null,
        status: hdr.status ?? record.status ?? "New",
        totalQty: hdr.totalQty ?? hdr.totalOrderedQuantity ?? record.totalOrderedQuantity ?? null,
        shipmentDate: ensureDateIso(hdr.shipmentDate ?? record.shipmentDate ?? null),
        source: hdr.source ?? record.source ?? null,

        totalWeight:
            hdr.totalWeight ??
            record.totalWeight ??
            record.shipmentDetails?.totalWeight ??
            0,

        totalVolume:
            hdr.totalVolume ??
            record.totalVolume ??
            record.shipmentDetails?.totalVolume ??
            0,


        sourceName: src.sourceName ?? src.name ?? src.locationName ?? record.sourceName ?? null,
        sourceAddressLine1: src.addressLine1 ?? null,
        sourceAddressLine2: src.addressLine2 ?? null,
        sourceAddressLine3: src.addressLine3 ?? null,
        sourceAddressLine4: src.addressLine4 ?? null,
        sourceCity: src.city ?? null,
        sourceState: src.state ?? null,
        sourceCountry: src.country ?? null,
        sourceContact: src.contact ?? src.phone ?? null,
        sourceEmail: src.email ?? null,

        destinationName: dest.name ?? dest.locationName ?? record.destinationName ?? null,
        destinationAddressLine1: dest.addressLine1 ?? null,
        destinationAddressLine2: dest.addressLine2 ?? null,
        destinationAddressLine3: dest.addressLine3 ?? null,
        destinationAddressLine4: dest.addressLine4 ?? null,
        destinationCity: dest.city ?? null,
        destinationState: dest.state ?? null,
        destinationCountry: dest.country ?? null,
        destinationContact: dest.contact ?? null,
        destinationEmail: dest.email ?? null,

        billToName: billTo.billToName ?? billTo.name ?? record.billToName ?? null,
        billToAddressLine1: billTo.addressLine1 ?? null,
        billToAddressLine2: billTo.addressLine2 ?? null,
        billToAddressLine3: billTo.addressLine3 ?? null,
        billToAddressLine4: billTo.addressLine4 ?? null,
        billToCity: billTo.city ?? null,
        billToState: billTo.state ?? null,
        billToCountry: billTo.country ?? null,
        billToContact: billTo.contact ?? billTo.phone ?? null,
        billToEmail: billTo.email ?? null,
        billToSameAsDestination: Boolean(billTo.billToSameAsDestination ?? record.billToSameAsDestination ?? false),

        carrierName: carr.carrierName ?? carr.name ?? null,
        carrierAddressLine1: carr.addressLine1 ?? carr.addressLine1 ?? null,
        carrierAddressLine2: carr.addressLine2 ?? carr.addressLine2 ?? null,
        carrierCity: carr.city ?? carr.carrierCity ?? null,
        carrierState: carr.state ?? carr.carrierState ?? null,
        carrierCountry: carr.country ?? carr.carrierCountry ?? null,
        carrierContact: carr.contact ?? carr.phone ?? null,
        carrierEmail: carr.email ?? null,
        shippingMode: carr.shippingMode ?? null,
        awbNumber: carr.awbNumber ?? carr.awbNo ?? null,

        invoiceNumber: invoice.invoiceNumber ?? invoice.number ?? record.invoiceNumber ?? null,
        invoiceDate: ensureDateIso(invoice.invoiceDate ?? invoice.date ?? record.invoiceDate ?? null),
        invoiceType: invoice.invoiceType ?? record.invoiceType ?? null,
        invoiceAmount: invoice.invoiceAmount ?? record.invoiceAmount ?? null,
        paymentTerms: invoice.paymentTerms ?? null,
        invoice: { ...(invoice || {}) },

        vehicleNumber: loadObj.vehicleNumber ?? loadObj.vehicleNo ?? null,
        vehicleDetails: loadObj.vehicleDetails ?? null,
        driverDetails: loadObj.driverDetails ?? loadObj.driver ?? null,
        expectedDepartureDate: ensureDateIso(loadObj.expectedDepartureDate ?? loadObj.etd ?? null),
        actualDepartureDate: ensureDateIso(loadObj.actualDepartureDate ?? loadObj.atd ?? null),
        expectedArrivalDate: ensureDateIso(loadObj.expectedArrivalDate ?? loadObj.eta ?? null),
        actualArrivalDate: ensureDateIso(loadObj.actualArrivalDate ?? loadObj.ata ?? null),

        podDetails: record.pod?.podDetails ?? record.podDetails ?? null,
        podUpload: record.pod?.podUpload ?? record.podUpload ?? null,

        udf: { ...(udf || {}) },
        textUDF1: udf.textUDF1 ?? udf.textUdf1 ?? null,
        textUDF2: udf.textUDF2 ?? udf.textUdf2 ?? null,
        textUDF3: udf.textUDF3 ?? udf.textUdf3 ?? null,

        sourceDetail: { ...(src || {}) },
        destination: { ...(dest || {}) },
        carrier: { ...(carr || {}) },
        billTo: { ...(billTo || {}) },
        load: { ...(loadObj || {}) },

        createdBy: record.createdBy ?? udf.createdBy ?? null,
        createdAt: ensureDateIso(record.createdAt ?? hdr.createdAt ?? udf.createdAt ?? null),
        updatedBy: record.updatedBy ?? udf.updatedBy ?? null,
        updatedDate: ensureDateIso(record.updatedAt ?? hdr.updatedAt ?? udf.updatedDate ?? null),

        __viewOnly: false,
        _id: record._id ?? record.id ?? null,
    };

    const tableData = {};

    // Check if data has stopList structure (from fetchListById)
    const stopListRaw = Array.isArray(record.stopList) ? record.stopList : [];
    
    // Initialize stops array for initialValues
    const stopsArray = [];

    if (stopListRaw.length > 0) {
        // Process stopList structure
        stopListRaw.forEach((stop) => {
            const stopListNo = stop.stopListNo ?? stop.id ?? null;
            if (stopListNo === null) return;

            const stopGroupKey = `STOP_${stopListNo}`;
            const lineItems = Array.isArray(stop.lineItems) ? stop.lineItems : [];

            // Extract order number from lineItems (use first non-null salesOrder)
            const orderNumber = lineItems
                .map(li => li.salesOrder ?? li.soNumber ?? null)
                .find(so => so !== null && so !== undefined) ?? "";

            // Add stop to stops array
            stopsArray.push({
                id: stopListNo,
                orderNumber: String(orderNumber).trim()
            });

            // Process line items for this stop
            const stopTableRows = [];
            lineItems.forEach((it, idx) => {
                const expectedQty = toNum(it.orderedQty ?? it.expectedQty ?? it.quantity ?? it.qty ?? 0);
                const dispatchQty = toNum(it.dispatchQty ?? it.dispatchedQty ?? it.dispatched ?? 0);
                const openQty = (it.openQty !== undefined && it.openQty !== null) ? toNum(it.openQty) : Math.max(expectedQty - dispatchQty, 0);

                stopTableRows.push({
                    lineNo: it.lineNumber ?? String(idx + 1).padStart(4, "0"),
                    item: it.skuId ?? it.item ?? "",
                    skuId: it.skuId ?? it.item ?? "",
                    purchaseOrder: it.purchaseOrder ?? null,
                    salesOrder: it.salesOrder ?? it.soNumber ?? null,
                    salesOrderId: it.salesOrderId ?? null,
                    description: it.description ?? it.itemDescription ?? "",
                    pack: it.pack ?? "STD",
                    uom: it.uom ?? "EA",
                    expectedQty,
                    orderedQty: expectedQty,
                    itemPOTolerance: it.itemPOTolerance ?? it.tolerance ?? 0,
                    openQty,
                    dispatchQty,
                    qcRequired: it.qcRequired ?? false,
                    receivedQty: it.receivedQty ?? null,
                    receivedDate: it.receivedDate ?? null,
                    grNumber: it.grNumber ?? null,
                    itemWeight: it.itemWeight ?? null,
                    itemVolume: it.itemVolume ?? null,
                    lpn: it.lpn ?? null,
                    lotBatch: it.lotBatch ?? null,
                    manufactureDate: it.manufactureDate ?? null,
                    expiryDate: it.expiryDate ?? null,
                    deliverByDate: it.deliverByDate ?? null,
                    bestByDate: it.bestByDate ?? null,
                    itemUDF: it.itemUDF ?? null,
                    rowIndex: it.rowIndex ?? idx,
                    lineItemId: it.lineItemId ?? it.id ?? null,
                    soDetails: Array.isArray(it.soDetails) ? it.soDetails : null,
                    __originalLine__: it,
                });
            });

            // Process delivery states for this stop's rows
            stopTableRows.forEach(row => {
                const original = row.__originalLine__ ?? row;
                const deliveryState = helper_getLineDeliveryState(original);
                const expected = toNum(original.orderedQty ?? original.expectedQty ?? original.quantity ?? 0);
                const delivered = toNum(original.receivedQty ?? original.deliveredQty ?? original.delivered ?? 0);
                const dispatched = toNum(original.dispatchedQty ?? original.dispatchQty ?? original.dispatched ?? 0);
                row.deliveryState = deliveryState;
                row._derived = { expectedQty: expected, deliveredQty: delivered, dispatchedQty: dispatched };
            });

            if (stopTableRows.length > 0) {
                tableData[stopGroupKey] = stopTableRows;
            }
        });

        // Update initialValues with stops array
        if (stopsArray.length > 0) {
            initialValues.stops = stopsArray;
            // Also set orderNumbers for backward compatibility (first stop's order number)
            if (stopsArray[0]?.orderNumber) {
                initialValues.orderNumbers = stopsArray[0].orderNumber;
            }
        }
    } else {
        // Fallback to original flat lineItems processing
        const itemsRaw = Array.isArray(record.lineItems)
            ? record.lineItems
            : Array.isArray(record.items)
                ? record.items
                : [];

        const items = (itemsRaw || []).map(it => (it && it.detail ? it.detail : it));

        (items || []).forEach((it, idx) => {
            const groupKey = it.purchaseOrder ? `PO::${it.purchaseOrder}` : `SKU::${it.skuId ?? it.item ?? `ROW${idx + 1}`}`;
            if (!tableData[groupKey]) tableData[groupKey] = [];

            const expectedQty = toNum(it.orderedQty ?? it.expectedQty ?? it.quantity ?? it.qty ?? 0);
            const dispatchQty = toNum(it.dispatchQty ?? it.dispatchedQty ?? it.dispatched ?? 0);
            const openQty = (it.openQty !== undefined && it.openQty !== null) ? toNum(it.openQty) : Math.max(expectedQty - dispatchQty, 0);

            tableData[groupKey].push({
                lineNo: it.lineNumber ?? String(idx + 1).padStart(4, "0"),
                item: it.skuId ?? it.item ?? "",
                skuId: it.skuId ?? it.item ?? "",
                purchaseOrder: it.purchaseOrder ?? null,
                salesOrder: it.salesOrder ?? it.soNumber ?? null,
                salesOrderId: it.salesOrderId ?? null,
                description: it.description ?? it.itemDescription ?? "",
                pack: it.pack ?? "STD",
                uom: it.uom ?? "EA",
                expectedQty,
                itemPOTolerance: it.itemPOTolerance ?? it.tolerance ?? 0,
                openQty,
                dispatchQty,
                qcRequired: it.qcRequired ?? false,
                receivedQty: it.receivedQty ?? null,
                receivedDate: it.receivedDate ?? null,
                lineItemId: it.lineItemId ?? it.id ?? null,
                soDetails: Array.isArray(it.soDetails) ? it.soDetails : null,
                __originalLine__: it,
            });
        });

        Object.keys(tableData).forEach(groupKey => {
            tableData[groupKey] = (tableData[groupKey] || []).map(row => {
                const original = row.__originalLine__ ?? row;
                const deliveryState = helper_getLineDeliveryState(original);
                const expected = toNum(original.orderedQty ?? original.expectedQty ?? original.quantity ?? 0);
                const delivered = toNum(original.receivedQty ?? original.deliveredQty ?? original.delivered ?? 0);
                const dispatched = toNum(original.dispatchedQty ?? original.dispatchQty ?? original.dispatched ?? 0);
                return {
                    ...row,
                    deliveryState,
                    _derived: { expectedQty: expected, deliveredQty: delivered, dispatchedQty: dispatched },
                };
            });
        });
    }

    tableData.__lastAddedTable = Object.keys(tableData).filter(k => !k.startsWith("__")).slice(-1)[0] || undefined;

    return { initialValues, tableData };
}

/* ---------- buildRecordFromShallow (unchanged) ---------- */
function buildRecordFromShallow(shallow = {}) {
    if (!shallow || typeof shallow !== 'object') return null;
    if (shallow.shipmentDetails || shallow.header || shallow.lineItems) return shallow;

    const hdr = {};
    if (shallow.shipmentNumber) hdr.shipmentNumber = shallow.shipmentNumber;
    if (shallow.loadId) hdr.loadId = shallow.loadId;
    if (shallow.externalShipmentNumber) hdr.externalShipmentNumber = shallow.externalShipmentNumber;
    if (shallow.status) hdr.status = shallow.status;
    if (shallow.totalQty || shallow.totalOrderedQuantity) hdr.totalOrderedQuantity = shallow.totalQty ?? shallow.totalOrderedQuantity;
    if (shallow.shipmentDate) hdr.shipmentDate = shallow.shipmentDate;

    const sourceDetail = shallow.sourceDetail || shallow.source || {
        sourceName: shallow.sourceName || shallow.sourceLocationName || shallow.source || undefined,
        addressLine1: shallow.sourceAddressLine1 || undefined,
        city: shallow.sourceCity || undefined,
        state: shallow.sourceState || undefined,
        contact: shallow.sourceContact || undefined,
        email: shallow.sourceEmail || undefined,
    };

    const destination = shallow.destination || {
        name: shallow.destinationName || shallow.destinationLocationName || undefined,
        addressLine1: shallow.destinationAddressLine1 || undefined,
        city: shallow.destinationCity || undefined,
        state: shallow.destinationState || undefined,
    };

    const billTo = shallow.billTo || {
        billToName: shallow.billToName || undefined,
        addressLine1: shallow.billToAddressLine1 || undefined,
        city: shallow.billToCity || undefined,
        contact: shallow.billToContact || undefined,
    };

    let lineItems = [];
    if (Array.isArray(shallow.lineItems) && shallow.lineItems.length) {
        lineItems = shallow.lineItems;
    } else if (Array.isArray(shallow._raw?.lineItems)) {
        lineItems = shallow._raw.lineItems;
    } else if (Array.isArray(shallow.items)) {
        lineItems = shallow.items;
    } else if (Array.isArray(shallow._raw?.items)) {
        lineItems = shallow._raw.items;
    } else if (shallow.skuId && (shallow.orderedQty || shallow.expectedQty)) {
        lineItems = [{
            skuId: shallow.skuId,
            orderedQty: shallow.orderedQty ?? shallow.expectedQty ?? 0,
            openQty: shallow.openQty ?? (shallow.orderedQty ?? 0)
        }];
    }

    const derivedCarrier =
        shallow.carrier ??
        (shallow.loading && (shallow.loading.carrier ?? shallow.loading)) ??
        (shallow.loading?.carrier ? shallow.loading.carrier : undefined) ??
        (shallow.carrierName ? { carrierName: shallow.carrierName } : undefined) ??
        undefined;

    const rec = {
        _id: shallow._id ?? shallow.id ?? undefined,
        shipmentDetails: Object.keys(hdr).length ? hdr : { shipmentNumber: shallow.shipmentNumber ?? undefined },
        sourceDetail: sourceDetail,
        destination: destination,
        billTo: billTo,
        carrier: (typeof derivedCarrier === 'string' ? { carrierName: derivedCarrier } : (derivedCarrier || {})),
        load: shallow.load ?? (shallow.vehicleNumber || shallow.driverDetails ? { vehicleNumber: shallow.vehicleNumber, driverDetails: shallow.driverDetails } : {}),
        lineItems: lineItems,
        _raw: shallow,
    };

    return rec;
}

/* ---------- Component ---------- */
export default function ShippingManagement({ initialValues = {}, onCancel, onSubmit }) {
    const [isCreating, setIsCreating] = useState(false);
    const [activeShipment, setActiveShipment] = useState(null);
    const [listVersion, setListVersion] = useState(0);
    const [shipmentsData, setShipmentsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showConfirmButton, setShowConfirmButton] = useState(false);

    const dispatch = useDispatch();

    // console.log("ShippingManagement rendered with initialValues:", initialValues);

    // refs to avoid re-running effect on shipmentsData mutation
    const shipmentsDataRef = useRef(shipmentsData);
    const mountedRef = useRef(true);
    const currentRequestRef = useRef(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (currentRequestRef.current && typeof currentRequestRef.current.cancel === 'function') {
                try { currentRequestRef.current.cancel(); } catch (e) { /* ignore */ }
            }
        };
    }, []);

    useEffect(() => {
        shipmentsDataRef.current = shipmentsData;
    }, [shipmentsData]);

    // detect deep-link / injected open (e.g. called via hyperlink)
    const injectedFromOutside = Boolean(initialValues?.shipmentNumber);

    // is the current active view the deep-linked view?
    const isDeepLinkedView =
        injectedFromOutside &&
        !!activeShipment &&
        activeShipment.mode === "view" &&
        String(activeShipment.shipmentNumber || "") === String(initialValues?.shipmentNumber || "");

    // ---------- view/edit helpers (defined before the effect so effect can call them) ----------
    const handleViewShipment = useCallback(async(rec) => {

        console.log("handleViewShipment rec:....................", rec);
        if (!rec || typeof rec !== 'object') {
            toast.error("Invalid shipment record");
            return;
        }
        try {

            const data = await getShipmentById(rec._id);

            console.log("View shipment fetched data:", data);

           
            const { initialValues: normalizedInit, tableData } = normalizeShipmentApi(data);
            const initView = { ...normalizedInit, __viewOnly: true };

            dispatch(setLoadTable(tableData));

            setActiveShipment({
                mode: 'view',
                shipmentNumber: initView.shipmentNumber ?? initView._id ?? null,
                initialValues: initView
            });
            setIsCreating(false);
            setShowConfirmButton(false);
        } catch (err) {
            console.error("View shipment error", err);
            toast.error("Failed to open shipment");
        }
    }, [dispatch]);

    const handleEditShipment = useCallback(async(row) => {
        // console.log("handleEditShipment row:", row);
        const rec = row.__raw || row || {};
        try {

            const data = await getShipmentById(rec._id);

           
            console.log("Edit shipment record to normalize:", data);
            const { initialValues: normalizedInit, tableData } = normalizeShipmentApi(data);
            const initValues = { ...normalizedInit };
            if (initValues.__viewOnly) delete initValues.__viewOnly;

            dispatch(setLoadTable(tableData));
            setActiveShipment({
                mode: 'edit',
                shipmentNumber: initValues.shipmentNumber ?? initValues._id ?? null,
                initialValues: initValues
            });
            setIsCreating(false);
            setShowConfirmButton(false);
        } catch (err) {
            console.error("handleEditShipment error:", err);
            toast.error("Failed to open shipment for edit");
        }
    }, [dispatch]);

    // handleConfirmDelivery remains same as your earlier implementation
    const handleConfirmDelivery = useCallback((recOrKey, maybePayload) => {
        if (!recOrKey) {
            toast.error("Invalid record for Confirm Delivery");
            return;
        }

        try {
            let record = null;

            if (typeof recOrKey === "object") {
                record = recOrKey.__raw ? recOrKey.__raw : recOrKey;
                if (maybePayload && Array.isArray(maybePayload.__lineItems)) {
                    record = {
                        ...record,
                        lineItems: Array.isArray(record?.lineItems) && record.lineItems.length > 0
                            ? record.lineItems
                            : maybePayload.__lineItems,
                    };
                }
            } else {
                record =
                    shipmentsDataRef.current.find(
                        (s) => s.shipmentNumber === recOrKey || s._id === recOrKey
                    ) || null;

                if (!record) {
                    record = {
                        shipmentDetails: { shipmentNumber: recOrKey },
                        lineItems: [],
                    };
                }
            }

            let { initialValues: normalizedInit, tableData } = normalizeShipmentApi(record);

            const hasRows =
                tableData &&
                Object.keys(tableData).some(
                    (k) => !k.startsWith("__") && Array.isArray(tableData[k]) && tableData[k].length > 0
                );

            if (!hasRows && maybePayload && Array.isArray(maybePayload.__lineItems) && maybePayload.__lineItems.length > 0) {
                const patched = normalizeShipmentApi({
                    shipmentDetails: {
                        shipmentNumber:
                            record?.shipmentDetails?.shipmentNumber ??
                            record?.shipmentNumber ??
                            null,
                        _id: record?._id ?? record?.shipmentDetails?._id ?? null,
                    },
                    lineItems: maybePayload.__lineItems,
                });
                normalizedInit = patched.initialValues;
                tableData = patched.tableData;
            }

            const mergedInit = { ...normalizedInit };
            if (maybePayload && typeof maybePayload === "object") {
                if (maybePayload.podDetails !== undefined) {
                    mergedInit.podDetails = maybePayload.podDetails;
                }
                if (maybePayload.podUpload !== undefined) {
                    const existing = Array.isArray(mergedInit.podUpload)
                        ? mergedInit.podUpload
                        : mergedInit.podUpload
                            ? [mergedInit.podUpload]
                            : [];
                    mergedInit.podUpload = Array.isArray(maybePayload.podUpload)
                        ? [...existing, ...maybePayload.podUpload]
                        : maybePayload.podUpload
                            ? [...existing, maybePayload.podUpload]
                            : existing;
                }
                if (maybePayload.__openConfirmDelivery) mergedInit.__openConfirmDelivery = true;
                if (maybePayload.__viewOnly) mergedInit.__viewOnly = true;
            }

            const totalRows = Object.values(tableData || {})
                .filter((v) => Array.isArray(v))
                .reduce((acc, arr) => acc + arr.length, 0);
            // console.log("[Confirm] tableData groups:", Object.keys(tableData || {}));
            // console.log("[Confirm] total rows:", totalRows);

            dispatch(setLoadTable(tableData));

            setActiveShipment({
                mode: "confirm",
                shipmentNumber: mergedInit.shipmentNumber ?? mergedInit._id ?? null,
                initialValues: mergedInit,
            });
            setIsCreating(false);
            setShowConfirmButton(true);
        } catch (err) {
            console.error("handleConfirmDelivery error", err);
            toast.error("Failed to open Confirm Delivery");
        }
    }, [dispatch]);

    // --------------------- Main effect: respond to incoming initialValues ---------------------
    useEffect(() => {
        const snRaw = initialValues?.shipmentNumber;
        const sn = snRaw ? String(snRaw).trim() : "";
        const providedDetails = initialValues?.shipmentDetails ?? initialValues?.clickedRow ?? null;

        // guard: if providedDetails looks like a sales-order flattened row, don't treat it as a full shipment object
        const looksLikeSoRow = providedDetails && (
            providedDetails.soNumber ||
            providedDetails.salesOrder ||
            (providedDetails.shipmentHistory && !providedDetails.shipmentDetails)
        );

        if (providedDetails && typeof providedDetails === "object" && Object.keys(providedDetails).length > 0 && !looksLikeSoRow) {
            const maybeFull = providedDetails.shipmentDetails || providedDetails.header || providedDetails.lineItems
                ? providedDetails
                : buildRecordFromShallow(providedDetails) || providedDetails;

            handleViewShipment(maybeFull);
            return;
        }

        if (!sn) return;

        // local cache lookup via ref (no dep on shipmentsData to avoid loop)
        const localRec = (shipmentsDataRef.current || []).find(
            (s) =>
                (s?.shipmentDetails?.shipmentNumber && String(s.shipmentDetails.shipmentNumber).trim() === sn) ||
                (s?.shipmentNumber && String(s.shipmentNumber).trim() === sn) ||
                (s?._id && String(s._id).trim() === sn)
        );
        if (localRec) {
            handleViewShipment(localRec);
            return;
        }

        let cancelled = false;
        const requestId = Symbol("fetchShipmentReq");
        currentRequestRef.current = { id: requestId, cancel: () => { cancelled = true; } };

        (async () => {
            setLoading(true);
            try {
                // === NEW: use your fetchShipment service and search data array for exact match ===
                const resp = await fetchShipment({ shipmentNumber: sn });
                if (cancelled) return;

                // Expecting shapes like:
                // { success: true, message: '', data: [ {...}, {...} ] }
                // or array of records, or single object
                let records = [];
                if (!resp) {
                    records = [];
                } else if (Array.isArray(resp)) {
                    records = resp;
                } else if (resp.data && Array.isArray(resp.data)) {
                    records = resp.data;
                } else if (resp.data && typeof resp.data === "object") {
                    records = [resp.data];
                } else if (typeof resp === "object") {
                    // maybe single record object
                    records = [resp];
                }

                // helper to extract canonical shipment identifier from a record
                const canonical = (r) =>
                    String((r?.shipmentNumber ?? r?.shipmentDetails?.shipmentNumber ?? r?._id ?? "") || "").trim();

                // find exact match
                let record = null;
                if (records.length > 0) {
                    record = records.find(r => canonical(r) === sn) || null;
                    // fallback: if not found but only one record returned, use it
                    if (!record && records.length === 1) record = records[0];
                    // fallback: try contains match (case-insensitive)
                    if (!record) {
                        const low = sn.toLowerCase();
                        record = records.find(r => canonical(r).toLowerCase().includes(low)) || null;
                    }
                }

                if (cancelled) return;

                if (!record || (typeof record === "object" && Object.keys(record).length === 0)) {
                    toast.error(`Shipment ${sn} not found (empty)`);
                    if (mountedRef.current && !cancelled) handleViewShipment({ shipmentDetails: { shipmentNumber: sn }, lineItems: [] });
                } else {
                    if (mountedRef.current && !cancelled) {
                        handleViewShipment(record);
                        // cache it locally so next opens are instant
                        setShipmentsData(prev => {
                            const next = Array.isArray(prev) ? [record, ...prev] : [record];
                            shipmentsDataRef.current = next;
                            return next;
                        });
                    }
                }
            } catch (err) {
                if (!cancelled) {
                    console.error("Error fetching shipment by number:", err);
                    toast.error(`Failed to fetch shipment ${sn}: ${err?.message || err}`);
                    if (mountedRef.current) handleViewShipment({ shipmentDetails: { shipmentNumber: sn }, lineItems: [] });
                }
            } finally {
                if (!cancelled && mountedRef.current) setLoading(false);
                if (currentRequestRef.current && currentRequestRef.current.id === requestId) currentRequestRef.current = null;
            }
        })();

        return () => {
            cancelled = true;
            if (currentRequestRef.current && typeof currentRequestRef.current.cancel === 'function') currentRequestRef.current.cancel();
        };
    }, [initialValues?.shipmentNumber, initialValues?.shipmentDetails, initialValues?.clickedRow, handleViewShipment]);

    const closeForm = () => {
        setActiveShipment(null);
        setIsCreating(false);
        dispatch(setLoadTable({}));
        setListVersion(v => v + 1);
        setShowConfirmButton(false);
    };

    const handleSubmitForm = (createdRecord) => {
        try {
            setActiveShipment(null);
            setIsCreating(false);
            dispatch(setLoadTable({}));
            setListVersion(v => v + 1);
            if (createdRecord && typeof createdRecord === 'object') {
                setShipmentsData(prev => {
                    const next = [createdRecord, ...(prev || [])];
                    shipmentsDataRef.current = next;
                    return next;
                });
            }
            setShowConfirmButton(false);
            onSubmit?.(createdRecord);
        } catch (err) {
            console.error("handleSubmitForm error:", err);
        }
    };

    const onlyFormVisible = isCreating || !!activeShipment;

    return (
        <div>
            <div className="card shadow-lg main-card" style={{ position: 'relative' }}>
                {!isDeepLinkedView && (
                    <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 h6 fw-semibold">
                            {activeShipment
                                ? (activeShipment.mode === 'view' ? 'View Shipment' : 'Edit Shipment')
                                : isCreating ? 'Create Shipment Order'
                                    : 'Shipment Order Details'}
                        </h4>
                    </div>
                )}

                {(isCreating || activeShipment) && !isDeepLinkedView && (
                    <nav aria-label="breadcrumb" className="px-3 py-2 border-bottom bg-light mb-2">
                        <ol
                            className="breadcrumb mb-0 align-items-center"
                            style={{ ["--bs-breadcrumb-divider"]: "'/'" }}
                        >
                            <li className="breadcrumb-item">
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-decoration-none text-muted"
                                    onClick={() => {
                                        setIsCreating(false);
                                        setActiveShipment(null);
                                        dispatch(setLoadTable({}));
                                        setListVersion((v) => v + 1);
                                        setShowConfirmButton(false);
                                    }}
                                >
                                    Shipment Order
                                </button>
                            </li>

                            <li
                                className="breadcrumb-item active fw-semibold text-primary"
                                aria-current="page"
                            >
                                {isCreating && !activeShipment
                                    ? "Create Shipment Order"
                                    : activeShipment?.mode === "edit"
                                        ? "Edit Shipment"
                                        : activeShipment?.mode === "confirm"
                                            ? "Confirm Shipment"
                                            : activeShipment?.mode === "view"
                                                ? "View Shipment"
                                                : "Shipment Details"}
                            </li>

                        </ol>
                    </nav>
                )}

                <div className="card-body">
                    {!onlyFormVisible ? (
                        <ShipmentList
                            onViewShipment={handleViewShipment}
                            onEditShipment={handleEditShipment}
                            onConfirmDelivery={handleConfirmDelivery}
                            refreshKey={listVersion}
                            setShowConfirmButton={setShowConfirmButton}
                            showCreateButton={true}
                            createButtonText="Create New Shipment Order"
                            createButtonIcon="bi-plus-circle"
                            onCreateClick={() => {
                                setIsCreating(true);
                                dispatch(setLoadTable({}));
                            }}
                            onDataRefresh={() => setListVersion(v => v + 1)}
                        />
                    ) : (
                        <>
                            {isCreating && !activeShipment && (
                                <CreateShippingTabs
                                    initialValues={{}}
                                    onCancel={closeForm}
                                    onSubmit={handleSubmitForm}
                                    isEdit={false}
                                    viewOnly={false}
                                />
                            )}

                            {activeShipment && (
                                <CreateShippingTabs
                                    initialValues={activeShipment.initialValues}
                                    onCancel={closeForm}
                                    onSubmit={handleSubmitForm}
                                    isEdit={activeShipment.mode === 'edit' || activeShipment.mode === 'confirm'}
                                    viewOnly={activeShipment.mode === 'view'}
                                    showConfirmButton={showConfirmButton}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
